import React, { useState, useMemo } from 'react';
import './Marketplace.css';

/**
 * RentalCalendar Component
 * Interactive calendar for selecting rental date ranges
 */
export default function RentalCalendar({ 
  bookedDates = [], 
  onDateSelect, 
  selectedDates,
  minDays = 1,
  maxDays = 30,
  pricePerDay = 0
}) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectionStart, setSelectionStart] = useState(null);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Generate calendar days for current month view
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    
    // Add days from previous month
    const startDayOfWeek = firstDay.getDay();
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({
        date,
        dateString: formatDateString(date),
        isOtherMonth: true,
        isToday: false
      });
    }
    
    // Add days from current month
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      days.push({
        date,
        dateString: formatDateString(date),
        isOtherMonth: false,
        isToday: date.getTime() === today.getTime()
      });
    }
    
    // Add days from next month to complete the grid
    const remainingDays = 42 - days.length; // 6 rows × 7 days
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        date,
        dateString: formatDateString(date),
        isOtherMonth: true,
        isToday: false
      });
    }
    
    return days;
  }, [currentMonth]);

  function formatDateString(date) {
    return date.toISOString().split('T')[0];
  }

  function formatDisplayDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  }

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const isDateBooked = (dateString) => {
    return bookedDates.includes(dateString);
  };

  const isDateInPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isDateSelected = (dateString) => {
    if (!selectedDates?.start || !selectedDates?.end) {
      return dateString === selectedDates?.start;
    }
    
    const date = new Date(dateString);
    const start = new Date(selectedDates.start);
    const end = new Date(selectedDates.end);
    
    return date >= start && date <= end;
  };

  const isDateInRange = (dateString) => {
    if (!selectionStart || selectedDates?.end) return false;
    
    // During active selection
    return false;
  };

  const handleDayClick = (day) => {
    if (day.isOtherMonth || isDateBooked(day.dateString) || isDateInPast(day.date)) {
      return;
    }

    if (!selectionStart) {
      // First click - start selection
      setSelectionStart(day.dateString);
      onDateSelect({ start: day.dateString, end: null });
    } else {
      // Second click - complete selection
      const start = new Date(selectionStart);
      const end = day.date;
      
      if (end < start) {
        // If clicked date is before start, swap them
        onDateSelect({ start: day.dateString, end: selectionStart });
      } else {
        onDateSelect({ start: selectionStart, end: day.dateString });
      }
      setSelectionStart(null);
    }
  };

  const clearSelection = () => {
    setSelectionStart(null);
    onDateSelect({ start: null, end: null });
  };

  const calculateDays = () => {
    if (!selectedDates?.start || !selectedDates?.end) return 0;
    
    const start = new Date(selectedDates.start);
    const end = new Date(selectedDates.end);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  };

  const calculateTotal = () => {
    return calculateDays() * pricePerDay;
  };

  const getDayClasses = (day) => {
    const classes = ['calendar-day'];
    
    if (day.isOtherMonth) classes.push('other-month');
    if (day.isToday) classes.push('today');
    if (isDateBooked(day.dateString)) classes.push('booked');
    if (isDateInPast(day.date) && !day.isOtherMonth) classes.push('disabled');
    if (isDateSelected(day.dateString)) classes.push('selected');
    if (day.dateString === selectedDates?.start || day.dateString === selectedDates?.end) {
      classes.push('selected');
    }
    
    // Check if date is in the selected range
    if (selectedDates?.start && selectedDates?.end) {
      const date = new Date(day.dateString);
      const start = new Date(selectedDates.start);
      const end = new Date(selectedDates.end);
      if (date > start && date < end) {
        classes.push('in-range');
      }
    }
    
    return classes.join(' ');
  };

  return (
    <div className="rental-calendar-section">
      <h3>📅 Select Rental Dates</h3>
      
      <div className="calendar-container">
        <div className="calendar-header">
          <span className="calendar-month">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </span>
          <div className="calendar-nav">
            <button onClick={goToPreviousMonth}>←</button>
            <button onClick={goToNextMonth}>→</button>
          </div>
        </div>

        <div className="calendar-grid">
          {/* Day headers */}
          {dayNames.map(day => (
            <div key={day} className="calendar-day-header">{day}</div>
          ))}
          
          {/* Calendar days */}
          {calendarDays.map((day, index) => (
            <div
              key={index}
              className={getDayClasses(day)}
              onClick={() => handleDayClick(day)}
            >
              {day.date.getDate()}
            </div>
          ))}
        </div>

        <div className="calendar-legend">
          <div className="legend-item">
            <div className="legend-color available"></div>
            <span>Available</span>
          </div>
          <div className="legend-item">
            <div className="legend-color selected"></div>
            <span>Selected</span>
          </div>
          <div className="legend-item">
            <div className="legend-color booked"></div>
            <span>Booked</span>
          </div>
        </div>
      </div>

      {/* Selection Info */}
      {(selectedDates?.start || selectedDates?.end) && (
        <div className="selected-dates">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h4>Selected Period</h4>
              <div className="date-range">
                {selectedDates?.start && selectedDates?.end ? (
                  <>
                    {formatDisplayDate(selectedDates.start)} → {formatDisplayDate(selectedDates.end)}
                  </>
                ) : selectedDates?.start ? (
                  <>
                    {formatDisplayDate(selectedDates.start)} → <span style={{ opacity: 0.5 }}>Select end date</span>
                  </>
                ) : null}
              </div>
              
              {selectedDates?.start && selectedDates?.end && (
                <>
                  <div className="rental-total">
                    {calculateDays()} days × €{pricePerDay}/day = <strong>€{calculateTotal().toLocaleString()}</strong>
                  </div>
                  
                  {calculateDays() < minDays && (
                    <div style={{ color: '#ff8a80', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                      ⚠️ Minimum rental period is {minDays} days
                    </div>
                  )}
                  
                  {calculateDays() > maxDays && (
                    <div style={{ color: '#ff8a80', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                      ⚠️ Maximum rental period is {maxDays} days
                    </div>
                  )}
                </>
              )}
            </div>
            
            <button 
              onClick={clearSelection}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <p className="form-hint" style={{ marginTop: '1rem', textAlign: 'center' }}>
        Click to select start date, then click again to select end date
      </p>
    </div>
  );
}
